import { createPortal } from 'react-dom';
import { Printer, X } from 'lucide-react';

import { CARGOS, data as formatarData, numero, percentual, reais } from '../lib/formato.ts';
import type { CabecalhoFechamento, LinhaResultado } from '../lib/tipos.ts';
import { Botao } from './ui.tsx';

/**
 * Relatório consolidado para a diretoria.
 *
 * O que uma diretoria quer saber não é a tabela: é quanto foi, de onde veio e
 * se está sob controle. Por isso a composição vem antes do detalhe — dá para
 * ler o total, a divisão por cargo e o efeito do balizador sem descer à lista
 * pessoa por pessoa.
 */
export function RelatorioExecutivo({
  fechamento,
  linhas,
  onFechar,
}: {
  fechamento: CabecalhoFechamento;
  linhas: LinhaResultado[];
  onFechar: () => void;
}) {
  const total = linhas.reduce((s, l) => s + l.totalFinal, 0);
  const somaBruta = linhas.reduce((s, l) => s + l.somaPremiacao, 0);
  const totalOcupacao = linhas.reduce((s, l) => s + l.ocupacaoValor, 0);
  const totalComparecimento = linhas.reduce((s, l) => s + l.compValor, 0);
  const efeitoBalizador = total - somaBruta;

  const atendentes = linhas.filter((l) => l.cargoCodigo === 'atendente');
  const agendado = atendentes.reduce((s, l) => s + l.compAgendado, 0);
  const atendido = atendentes.reduce((s, l) => s + l.compAtendido, 0);
  const taxaEquipe = agendado > 0 ? atendido / agendado : 0;

  const porCargo = Object.entries(
    linhas.reduce<Record<string, { pessoas: number; total: number }>>((acc, l) => {
      const atual = acc[l.cargoCodigo] ?? { pessoas: 0, total: 0 };
      acc[l.cargoCodigo] = { pessoas: atual.pessoas + 1, total: atual.total + l.totalFinal };
      return acc;
    }, {}),
  ).sort((a, b) => b[1].total - a[1].total);

  // ------------------------------------------------------------- faltas
  //
  // Entram na lista quem faltou E quem trabalhou menos que o mês cheio. São
  // coisas diferentes: alguém pode ter entrado no meio do mês sem ter faltado
  // um dia, e sair da lista faria o valor reduzido dessa pessoa parecer
  // inexplicável.
  const comOcorrencia = linhas
    .filter((l) => {
      const faltou = (l.faltasJustificadas ?? 0) > 0 || (l.faltasInjustificadas ?? 0) > 0;
      const diasReduzidos =
        l.diasTrabalhados !== null &&
        l.diasTrabalhados !== undefined &&
        l.diasBase !== null &&
        l.diasBase !== undefined &&
        l.diasTrabalhados < l.diasBase;
      return faltou || diasReduzidos;
    })
    .sort((a, b) => {
      const perdaA = (a.premiacaoAntesAjustes ?? a.totalFinal) - a.totalFinal;
      const perdaB = (b.premiacaoAntesAjustes ?? b.totalFinal) - b.totalFinal;
      return perdaB - perdaA;
    });

  const totalJustificadas = linhas.reduce((s, l) => s + (l.faltasJustificadas ?? 0), 0);
  const totalInjustificadas = linhas.reduce((s, l) => s + (l.faltasInjustificadas ?? 0), 0);

  // O quanto os ajustes tiraram do total. Sai da diferença entre o valor
  // apurado antes deles e o valor pago — não de somar desconto com fator, que
  // dependeria da ordem em que foram aplicados.
  const efeitoDosAjustes = linhas.reduce(
    (s, l) => s + ((l.premiacaoAntesAjustes ?? l.totalFinal) - l.totalFinal),
    0,
  );

  // Quem zerou por falta: é a informação que a diretoria pergunta primeiro.
  const zerados = comOcorrencia.filter(
    (l) => l.totalFinal === 0 && (l.premiacaoAntesAjustes ?? 0) > 0,
  );

  const ordenadas = [...linhas].sort((a, b) => b.totalFinal - a.totalFinal);
  const soAtendentes = ordenadas.filter((l) => l.cargoCodigo === 'atendente');
  const maiores = soAtendentes.slice(0, 3);
  const menores = soAtendentes.slice(-3).reverse();

  return createPortal(
    <div className="impressao-raiz fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 p-4 print:static print:bg-transparent print:p-0">
      <div className="mx-auto max-w-4xl bg-white p-10 font-sans shadow-lg print:max-w-none print:p-0 print:shadow-none">
        <div className="mb-8 flex items-start justify-between gap-4 print:hidden">
          <p className="text-xs text-slate-500">
            Use "Imprimir" e escolha <strong>Salvar como PDF</strong>.
          </p>
          <div className="flex gap-2">
            <Botao tipo="secundario" onClick={() => window.print()}>
              <Printer className="size-4" />
              Imprimir
            </Botao>
            <button
              onClick={onFechar}
              title="Fechar"
              className="rounded p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* ---------------------------------------------------------- capa */}
        <header className="evitar-quebra border-b-4 border-slate-900 pb-5">
          <div className="flex items-baseline justify-between gap-6">
            <div>
              <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
                Skinlaser · Central de Agendamentos
              </p>
              <h1 className="mt-2 font-serif text-3xl leading-tight font-medium text-slate-900">
                Relatório de Premiação
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Competência {fechamento.competencia} · apuração de{' '}
                {formatarData(fechamento.periodoInicio)} a {formatarData(fechamento.periodoFim)}
              </p>
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] tracking-[0.15em] text-slate-500 uppercase">
                Total do mês
              </p>
              <p className="num mt-1 text-3xl font-semibold text-slate-900">{reais(total)}</p>
            </div>
          </div>
        </header>

        {/* ------------------------------------------------- indicadores */}
        <section className="evitar-quebra grid grid-cols-4 gap-px border-b border-slate-200 bg-slate-200">
          <Kpi rotulo="Pessoas premiadas" valor={String(linhas.filter((l) => l.totalFinal > 0).length)} nota={`de ${linhas.length} elegíveis`} />
          <Kpi rotulo="Taxa de ocupação" valor={percentual(fechamento.taxaOcupacao)} nota="meta de equipe" />
          <Kpi
            rotulo="Comparecimento da equipe"
            valor={percentual(taxaEquipe)}
            nota={`${numero(atendido)} de ${numero(agendado)} consultas`}
          />
          <Kpi
            rotulo="Média por pessoa"
            valor={reais(linhas.length > 0 ? total / linhas.length : 0)}
          />
        </section>

        {/* -------------------------------------------------- composição */}
        <section className="evitar-quebra py-7">
          <Titulo>Composição do valor</Titulo>
          <dl className="mt-3 space-y-1.5 text-sm">
            <Linha rotulo="Premiação por taxa de ocupação da agenda" valor={reais(totalOcupacao)} />
            <Linha rotulo="Premiação por taxa de comparecimento" valor={reais(totalComparecimento)} />
            <Linha rotulo="Subtotal apurado" valor={reais(somaBruta)} forte />
            <Linha
              rotulo="Efeito do balizador de produção"
              valor={`${efeitoBalizador >= 0 ? '+' : '−'} ${reais(Math.abs(efeitoBalizador))}`}
              nota={
                efeitoBalizador >= 0
                  ? 'a produção da equipe elevou o valor apurado'
                  : 'a produção abaixo da média reduziu o valor apurado'
              }
            />
            <Linha rotulo="Total pago" valor={reais(total)} destaque />
          </dl>
        </section>

        {/* ---------------------------------------------------- por cargo */}
        <section className="evitar-quebra border-t border-slate-200 py-7">
          <Titulo>Distribuição por cargo</Titulo>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-slate-300 text-left font-mono text-[10px] tracking-wider text-slate-500 uppercase">
                <th className="pb-1.5">Cargo</th>
                <th className="pb-1.5 text-right">Pessoas</th>
                <th className="pb-1.5 text-right">Total</th>
                <th className="pb-1.5 text-right">Média</th>
                <th className="pb-1.5 text-right">Participação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {porCargo.map(([cargo, v]) => (
                <tr key={cargo}>
                  <td className="py-1.5">{CARGOS[cargo] ?? cargo}</td>
                  <td className="num py-1.5">{v.pessoas}</td>
                  <td className="num py-1.5">{reais(v.total)}</td>
                  <td className="num py-1.5">{reais(v.total / v.pessoas)}</td>
                  <td className="num py-1.5 text-slate-600">
                    {percentual(total > 0 ? v.total / total : 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* --------------------------------------------------- destaques */}
        {soAtendentes.length >= 4 && (
          <section className="evitar-quebra grid grid-cols-2 gap-8 border-t border-slate-200 py-7">
            <div>
              <Titulo>Maiores valores — atendentes</Titulo>
              <ol className="mt-3 space-y-1.5 text-sm">
                {maiores.map((l, i) => (
                  <li key={l.nome} className="flex justify-between gap-3">
                    <span className="text-slate-700">
                      <span className="font-mono text-xs text-slate-400">{i + 1}º</span> {l.nome}
                    </span>
                    <span className="num whitespace-nowrap">{reais(l.totalFinal)}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <Titulo>Menores valores — atendentes</Titulo>
              <ol className="mt-3 space-y-1.5 text-sm">
                {menores.map((l, i) => (
                  <li key={l.nome} className="flex justify-between gap-3">
                    <span className="text-slate-700">
                      <span className="font-mono text-xs text-slate-400">{i + 1}º</span> {l.nome}
                    </span>
                    <span className="num whitespace-nowrap">{reais(l.totalFinal)}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}


        {/* ------------------------------------------------------ faltas */}
        <section className="border-t border-slate-200 py-7">
          <Titulo>Faltas e dias trabalhados</Titulo>

          {comOcorrencia.length === 0 ? (
            <p className="mt-3 text-sm text-slate-600">
              Nenhuma falta lançada e todo mundo com o mês completo. A ausência de ocorrências é
              registrada aqui de propósito: um relatório sem esta seção não diria se não houve
              falta ou se ninguém lançou.
            </p>
          ) : (
            <>
              <div className="mt-3 grid grid-cols-4 gap-6">
                <Kpi
                  rotulo="Pessoas com ocorrência"
                  valor={String(comOcorrencia.length)}
                  nota={`de ${linhas.length} no fechamento`}
                />
                <Kpi
                  rotulo="Faltas justificadas"
                  valor={String(totalJustificadas)}
                  nota="desconto em dias de salário"
                />
                <Kpi
                  rotulo="Faltas não justificadas"
                  valor={String(totalInjustificadas)}
                  nota={zerados.length === 0 ? 'nenhuma zerou premiação' : `${zerados.length} zeraram a premiação`}
                />
                <Kpi
                  rotulo="Efeito no total"
                  valor={reais(-efeitoDosAjustes)}
                  nota="faltas e proporcionalidade"
                />
              </div>

              <table className="mt-5 w-full text-[13px]">
                <thead>
                  <tr className="border-b border-slate-300 text-left font-mono text-[10px] tracking-wider text-slate-500 uppercase">
                    <th className="pb-1.5 pr-3">Nome</th>
                    <th className="pb-1.5 pr-3">Cargo</th>
                    <th className="pb-1.5 pr-3 text-right">Dias</th>
                    <th className="pb-1.5 pr-3 text-right">Just.</th>
                    <th className="pb-1.5 pr-3 text-right">Não just.</th>
                    <th className="pb-1.5 pr-3 text-right">Antes</th>
                    <th className="pb-1.5 pr-3 text-right">Efeito</th>
                    <th className="pb-1.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {comOcorrencia.map((l) => {
                    const antes = l.premiacaoAntesAjustes ?? l.totalFinal;
                    const efeito = antes - l.totalFinal;
                    const dias =
                      l.diasTrabalhados === null || l.diasTrabalhados === undefined
                        ? 'mês cheio'
                        : `${numero(l.diasTrabalhados)} de ${numero(l.diasBase ?? 0)}`;

                    return (
                      <tr key={l.nome}>
                        <td className="py-1.5 pr-3">{l.nome}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap text-slate-600">
                          {CARGOS[l.cargoCodigo] ?? l.cargoCodigo}
                        </td>
                        <td className="num py-1.5 pr-3 whitespace-nowrap text-slate-600">{dias}</td>
                        <td className="num py-1.5 pr-3">{l.faltasJustificadas || '—'}</td>
                        <td className="num py-1.5 pr-3">{l.faltasInjustificadas || '—'}</td>
                        <td className="num py-1.5 pr-3 whitespace-nowrap text-slate-600">
                          {reais(antes)}
                        </td>
                        <td className="num py-1.5 pr-3 whitespace-nowrap">
                          {efeito === 0 ? '—' : reais(-efeito)}
                        </td>
                        <td className="num py-1.5 text-right font-semibold whitespace-nowrap">
                          {reais(l.totalFinal)}
                          {l.totalFinal === 0 && antes > 0 && (
                            <span className="block font-mono text-[9px] tracking-wider text-slate-500 uppercase">
                              zerada
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-slate-900 font-semibold">
                    <td className="py-2 pr-3" colSpan={3}>
                      {comOcorrencia.length} {comOcorrencia.length === 1 ? 'pessoa' : 'pessoas'}
                    </td>
                    <td className="num py-2 pr-3">{totalJustificadas || '—'}</td>
                    <td className="num py-2 pr-3">{totalInjustificadas || '—'}</td>
                    <td className="num py-2 pr-3 whitespace-nowrap">
                      {reais(
                        comOcorrencia.reduce((s, l) => s + (l.premiacaoAntesAjustes ?? l.totalFinal), 0),
                      )}
                    </td>
                    <td className="num py-2 pr-3 whitespace-nowrap">
                      {reais(
                        -comOcorrencia.reduce(
                          (s, l) => s + ((l.premiacaoAntesAjustes ?? l.totalFinal) - l.totalFinal),
                          0,
                        ),
                      )}
                    </td>
                    <td className="num py-2 text-right whitespace-nowrap">
                      {reais(comOcorrencia.reduce((s, l) => s + l.totalFinal, 0))}
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
                <strong>Antes</strong> é o valor apurado até o balizador. <strong>Efeito</strong> é o que as faltas e
                a proporcionalidade por dias trabalhados tiraram dele — a diferença entre os dois,
                e não a soma das parcelas, porque a ordem em que os ajustes se aplicam altera o
                resultado. A falta justificada desconta dias de salário; a não justificada tira uma
                fração da premiação e zera a partir do limite configurado nas regras.
              </p>
            </>
          )}
        </section>

        {/* ----------------------------------------------------- detalhe */}
        <section className="border-t border-slate-200 py-7">
          <Titulo>Detalhamento por pessoa</Titulo>
          <table className="mt-3 w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-300 text-left font-mono text-[10px] tracking-wider text-slate-500 uppercase">
                <th className="pb-1.5 pr-3">Nome</th>
                <th className="pb-1.5 pr-3">Cargo</th>
                <th className="pb-1.5 pr-3 text-right">Compar.</th>
                <th className="pb-1.5 pr-3 text-right">Ocupação</th>
                <th className="pb-1.5 pr-3 text-right">Compar. R$</th>
                <th className="pb-1.5 pr-3">Balizador</th>
                <th className="pb-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ordenadas.map((l) => (
                <tr key={l.nome}>
                  <td className="py-1.5 pr-3">{l.nome}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-slate-600">
                    {CARGOS[l.cargoCodigo] ?? l.cargoCodigo}
                  </td>
                  <td className="num py-1.5 pr-3">{percentual(l.compTaxa)}</td>
                  <td className="num py-1.5 pr-3 whitespace-nowrap">{reais(l.ocupacaoValor)}</td>
                  <td className="num py-1.5 pr-3 whitespace-nowrap">{reais(l.compValor)}</td>
                  <td className="py-1.5 pr-3 text-[11px] whitespace-nowrap text-slate-600">
                    {l.balizRotulo === 'NÃO APLICÁVEL'
                      ? '—'
                      : `${l.balizRotulo.toLowerCase()} · ${percentual(l.balizFator)}`}
                  </td>
                  <td className="num py-1.5 text-right font-semibold whitespace-nowrap">
                    {reais(l.totalFinal)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-900 font-semibold">
                <td className="py-2 pr-3" colSpan={6}>
                  Total
                </td>
                <td className="num py-2 text-right">{reais(total)}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <footer className="evitar-quebra border-t border-slate-200 pt-4 text-[11px] leading-relaxed text-slate-500">
          <p>
            Regras aplicadas: {fechamento.regrasNome ?? '—'} · fechamento versão{' '}
            {fechamento.versao}
            {fechamento.fechadoEm === null ? '' : `, gravado em ${formatarData(fechamento.fechadoEm)}`}
            {fechamento.fechadoPor === null ? '' : ` por ${fechamento.fechadoPor}`}.
          </p>
          {fechamento.taxaOcupacaoOrigem !== null && (
            <p>Origem da taxa de ocupação: {fechamento.taxaOcupacaoOrigem}.</p>
          )}
          <p>
            Os valores foram congelados no momento do fechamento, junto da cópia das regras
            vigentes — uma alteração de regra posterior não altera este relatório.
          </p>
          <p className="mt-2 font-mono text-[10px] tracking-wider text-slate-400 uppercase">
            Documento confidencial · uso interno
          </p>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function Titulo({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-mono text-[10px] font-semibold tracking-[0.15em] text-slate-500 uppercase">
      {children}
    </h2>
  );
}

function Kpi({ rotulo, valor, nota }: { rotulo: string; valor: string; nota?: string }) {
  return (
    <div className="bg-white px-4 py-4">
      <p className="font-mono text-[9px] tracking-[0.12em] text-slate-500 uppercase">{rotulo}</p>
      <p className="num mt-1.5 text-lg font-semibold text-slate-900">{valor}</p>
      {nota !== undefined && <p className="mt-0.5 text-[11px] text-slate-500">{nota}</p>}
    </div>
  );
}

function Linha({
  rotulo,
  valor,
  nota,
  forte = false,
  destaque = false,
}: {
  rotulo: string;
  valor: string;
  nota?: string;
  forte?: boolean;
  destaque?: boolean;
}) {
  return (
    <div
      className={
        destaque
          ? 'flex items-baseline justify-between gap-4 border-t-2 border-slate-900 pt-2'
          : 'flex items-baseline justify-between gap-4 border-b border-dotted border-slate-200 pb-1.5'
      }
    >
      <dt className={forte || destaque ? 'font-medium text-slate-900' : 'text-slate-600'}>
        {rotulo}
        {nota !== undefined && (
          <span className="ml-2 text-[11px] font-normal text-slate-500">({nota})</span>
        )}
      </dt>
      <dd
        className={
          destaque
            ? 'num text-lg font-semibold whitespace-nowrap'
            : forte
              ? 'num font-semibold whitespace-nowrap'
              : 'num whitespace-nowrap'
        }
      >
        {valor}
      </dd>
    </div>
  );
}
